import { Component, inject, OnInit, signal } from '@angular/core';
import { NavController, Platform, ToastController } from '@ionic/angular';
import { of, Subject } from 'rxjs';
import { catchError, filter, switchMap, take, takeUntil, tap, timeout } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { describePostAuthFailure, LoginService, PostAuthDestination, SessionRejectedError } from 'src/app/services/login/login.service';
import { NetworkService } from 'src/app/services/network/network.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { ActivatedRoute } from '@angular/router';
import { Logger, LoggingService } from 'ionic-logging-service';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TenantService } from '@app/services/tenant/tenant.service';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { DialogService } from '@app/services/dialog/dialog.service';
import { ErrorService } from '@app/services/error/error.service';
import { LogoutService } from '@app/services/logout/logout.service';
import { PkceService } from '@app/services/pkce/pkce.service';
import { CLIENT_ID_BROWSER, CLIENT_ID_NATIVE, SIGN_IN_QUERY_PARAMS } from '@app/services/auth/auth.constants';
import { PostAuthNavigationService } from '@app/services/post-auth-navigation/post-auth-navigation.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-auth',
    templateUrl: './auth.page.html',
    styleUrls: ['./auth.page.scss'],
    standalone: false
})
export class AuthPage implements OnInit {
  private route = inject(ActivatedRoute);

  // Bootstrap-scoped despite the name: completed during startup, in verifyUserCredentials()
  // and checkForAcceptedEula(). takeUntil reacts to next() and never to complete(), so using it
  // for anything after bootstrap tears down nothing — eulaAccepted() is already in that state.
  // There is no ngOnDestroy; post-bootstrap work needs its own subject.
  public unSubscribe = new Subject<void>();
  public isCordova: boolean = this.platformService.isCordova();
  public isOnline = this.networkService.hasInternetAccess;
  public isLoading = signal(false);
  public showNoAccess = signal<boolean>(false);
  // Shown on the page when the signed-in user has no tenant access.
  public readonly noAccessMessage = 'No access to any tenants, try signing out and signing in with a different user';
  // Extra hint shown only on non-production environments (global users only exist there).
  public showGlobalUserHint = signal<boolean>(false);
  public readonly globalUserHintMessage = 'Remember: global users cannot access this app';
  public errorMessage: string = null;
  public showUnsupportedVersionMessage: boolean = false;
  public APP_ID: string = 'id1391512097';
  public APP_PACKAGE_NAME: string = 'net.printix.printixapp';
  public EULA_ACCEPTED_STORAGE_STRING = 'eula-accepted';
  public EULA_ACCEPTED_STORAGE_KEY = 'is-eula-accepted';
  public showEulaText = false;
  private registrationCode: string = null;
  private refreshTokenParam: string = null;
  private queryParams: { [key: string]: string | undefined };
  private signOutParam: string | null = null;
  private logger: Logger;
  private WEB_APP_SCHEME = 'https://';
  // Long enough to read a one-line explanation, short enough that the hand-off it delays
  // does not feel stuck. The redirect waits for it — see showErrorToast().
  private static readonly ERROR_TOAST_MS = 3000;
  // How long to wait for the caption before giving up on explaining. Short: the hand-off
  // matters more than the message, and this only elapses when the CDN fetch is stuck.
  private static readonly CAPTION_WAIT_MS = 2000;
  private urlTenantDomain: string;
  public  signInCode: string | null = null;
  public eulaHtml: string;

  constructor (
    private authService: AuthService,
    private dialogService: DialogService,
    private environmentService: EnvironmentService,
    private errorService: ErrorService,
    private loginService: LoginService,
    private logoutService: LogoutService,
    private loggingService: LoggingService,
    public navController: NavController,
    private networkService: NetworkService,
    private pageTitleService: PageTitleService,
    public platform: Platform,
    private pkceService: PkceService,
    private platformService: PlatformService,
    private postAuthNavigationService: PostAuthNavigationService,
    private storageService: StorageService,
    private tenantService: TenantService,
    private toastController: ToastController,
    private translateService: TranslateService,
    private httpClient: HttpClient
  ) {
    this.logger = this.loggingService.getLogger("[AuthPage]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  ngOnInit() {
    this.pageTitleService.setTitle('');
    this.platform.ready().then(() => {
      this.logger.info('ngOnInit() - Platform is ready');
      // Arrived here because the signed-in user has no tenant access (see
      // navigatePostAuth 'no-access'). Render the no-access state and skip the
      // bootstrap/credential flow so it does not re-trigger and re-pop the dialog.
      if (this.route.snapshot.queryParamMap.get('noAccess') === '1') {
        this.enterNoAccessState();
        return;
      }
      if (this.isCordova) {
        this.storageService.getDatabaseState().pipe(
          filter(isReady => isReady),
          take(1)
          // check localstorage for 'EULA accepted'
        ).subscribe(() => this.checkForAcceptedEula());
      } else {
        this.setUrlTenantdomain();
        this.setQueryParams();
      }
    });
  }

  private setUrlTenantdomain() {
    this.logger.info('setUrlTenantdomain()');
    this.urlTenantDomain = location.hostname;
    this.tenantService.setTenantInputValues(this.urlTenantDomain, null);
  }

  private checkForAcceptedEula(): void {
    this.logger.info('checkForAcceptedEula()');
    this.storageService.getItemFromLocalStorage(this.EULA_ACCEPTED_STORAGE_KEY, 'checkForAcceptedEula()')
    .pipe(takeUntil(this.unSubscribe))
    .subscribe((EULA_ACCEPTED) => {
      if (EULA_ACCEPTED === this.EULA_ACCEPTED_STORAGE_STRING) {
        this.setQueryParams();
      } else {
        this.showEulaText = true;
        this.loadEulaText();
      }
      this.unSubscribe.next();
      this.unSubscribe.complete();
    });
  }

  loadEulaText() {
    this.httpClient.get('assets/eula-text/eula.html', { responseType: 'text' }).subscribe(html => {
      this.eulaHtml = html;
    });
  }

  // #################################### //
  // ### NO TENANT ACCESS STATE ######### //
  // #################################### //

  /**
   * Renders the "no tenant access" state instead of the bootstrap flow.
   *
   * The user is authenticated but their tenant list came back empty, so there is
   * nowhere to route them. We stay on this page — rather than tenant-select — so
   * the side menu (sign out, send logs, help) stays reachable, and show a message
   * explaining the situation. On non-production environments an extra line reminds
   * testers that global users cannot access the app.
   */
  private enterNoAccessState(): void {
    const methodName = 'enterNoAccessState() ';
    this.logger.info(methodName);
    this.pageTitleService.setTitle('Auth');
    this.showNoAccess.set(true);
    this.isLoading.set(false);

    // Default to hiding the tester hint when the environment is unknown (e.g. a cold
    // /auth?noAccess=1 reload returns before environment resolution): treat unknown as
    // production so the non-prod-only hint can never leak onto production. It shows
    // only when PRODUCTION is explicitly false.
    const isProduction = this.environmentService.getEnvironmentEndpoint?.PRODUCTION !== false;
    this.showGlobalUserHint.set(!isProduction);
  }

  /**
   * Sign-out shortcut for the no-access state so the user need not open the menu.
   * Mirrors MenuComponent.handleSignOut(): revoke token + clear local state, then
   * route to the platform sign-out page which ends the auth-server SSO session
   * (web returns here with signOutParam=USER_SIGN_OUT → endSsoSession()).
   */
  public signOut(): void {
    const methodName = 'signOut() ';
    this.logger.info(methodName);
    // Spinner covers the hand-off (token revoke + state clear + navigation); native
    // only, as on web the '_self' redirect to the sign-in app tears down the page.
    if (this.isCordova) {
      this.dialogService.showLoadingSpinnerDialog(methodName).subscribe();
    }
    this.logoutService.signOutUser('user-initiated').pipe(take(1)).subscribe({
      next: () => this.completeSignOut(),
      error: (error: HttpErrorResponse) => {
        this.logger.error(methodName + 'signOutUser error: ' + JSON.stringify(error));
        // Complete the hand-off even if local state clearing fails.
        this.completeSignOut();
      }
    });
  }

  /**
   * Finishes the sign-out hand-off after local state is cleared.
   *
   * Native routes to '/sign-in' (a different route → LoginPage re-inits and owns the
   * native sign-out). On web we are ALREADY on '/auth', so navigating to '/auth' again
   * would be route-reused (IonicRouteStrategy) without re-running ngOnInit/setQueryParams
   * — endSsoSession() would never fire and the browser SSO cookie would survive. So end
   * the SSO session + hand off to the sign-in app directly. urlTenantDomain is (re)set
   * here because the noAccess=1 ngOnInit branch returns before setUrlTenantdomain() runs.
   */
  private completeSignOut(): void {
    const methodName = 'completeSignOut() ';
    this.logger.info(methodName);
    if (this.isCordova) {
      this.navController.navigateRoot(['/sign-in'], { queryParams: { signOutParam: 'USER_SIGN_OUT' } });
      return;
    }
    this.setUrlTenantdomain();
    this.endSsoSession();
  }

  public eulaAccepted() {
    this.storageService.addItemToLocalStorage(this.EULA_ACCEPTED_STORAGE_KEY, this.EULA_ACCEPTED_STORAGE_STRING)
    .pipe(takeUntil(this.unSubscribe))
    .subscribe(() => {
      this.showEulaText = false;
      this.setQueryParams();
    })
  }

  public eulaDenied() {
    this.showEulaText = false;
    navigator['app'].exitApp();
  }

  private setQueryParams(): void {
    this.logger.info('setQueryParams()');
    this.route.queryParams.pipe(take(1))
    .subscribe(params => {
      this.queryParams = params;
      this.signInCode = this.queryParams["code"] ? this.queryParams["code"] : null;
      this.registrationCode = this.queryParams['registrationCode'] ? this.queryParams['registrationCode'] : null;
      this.refreshTokenParam = this.getQueryParamFromHash('refreshToken');
      this.signOutParam = params['signOutParam'] ? params['signOutParam'] : null;
      if (this.signOutParam === 'USER_SIGN_OUT') {
        this.logger.info('signOutParam USER_SIGN_OUT');
        this.endSsoSession();
      } else if (this.signOutParam) {
        this.logger.info('signOutParam' + this.signOutParam);
        this.handleUnverifiedCredentials();
      } else {
        this.setEnvironmentPrefix();
      }
    });
  }

  private getQueryParamFromHash(param: string): string | null {
    const hash = window.location.hash;
    const queryString = hash.includes('?') ? hash.split('?')[1] : '';
    const params = new URLSearchParams(queryString);
    return params.get(param);
  }

  private setEnvironmentPrefix(): void {
    if (this.isCordova) {
      this.environmentService.setEnvironmentPrefixFromStorage().subscribe((environmentPrefix) => {
        // this.checkAppVersionState();
        // outcommented call to checkAppVersionState(), since it has not been used for years
        // and it needs a reworking to accomodate multiple datacenters, both for the endpoint and
        // also not to be checked so early in the app, before the currentEnvironmentEndpoints are set.
        this.verifyUserCredentials();
      });
    } else {
      if (!this.urlTenantDomain.includes('localhost')) {
        this.environmentService.setEnvironmentVariablesFromUrl(this.urlTenantDomain).subscribe((response) => {
          // this.checkAppVersionState();
          // outcommented call to checkAppVersionState(), since it has not been used for years
          // and it needs a reworking to accomodate multiple datacenters, both for the endpoint and
          // also not to be checked so early in the app, before the currentEnvironmentEndpoints are set.
          this.verifyUserCredentials();
        });
      } else {
        this.verifyUserCredentials();
      }
    }
  }

 private verifyUserCredentials(): void {
    const methodName = 'verifyUserCredentials() '
    this.logger.info(methodName);
    this.isLoading.set(true);
    if (this.refreshTokenParam) {
      this.logger.info(methodName + 'url includes "refreshToken="');
      this.logInFromTenantHub();
    } else {
      this.logger.info(methodName + 'url does NOT include "refreshToken="');
      this.authService.verifyUserCredentials()
      .pipe(
        takeUntil(this.unSubscribe),
        // Returning user — finding a stored refresh token means any
        // idpLoginToken in storage is leftover from a previous (incomplete)
        // activation chain. Clear it before resolvePostAuthNavigation$ runs so
        // the tenant-list call is not routed through internal-auth validation.
        switchMap(() => this.loginService.clearIdpLoginToken$())
      )
      .subscribe(() => {
        this.unSubscribe.next();
        this.unSubscribe.complete();
        this.authSuccessful();
      }, () => {
        this.unSubscribe.next();
        this.unSubscribe.complete();
        this.isLoading.set(false);
        if (this.signInCode) {
          this.handleUserAuthentication();
        } else {
          this.handleUnverifiedCredentials();
        }
      });
    }
  }

  // Final step of the sign-out flow on web: POST ENDPOINT_SIGN_OUT_BROWSER (via
  // LogoutService) so the auth server clears the SSO session cookie (200 OK, no
  // redirect), then route to the sign-in app ourselves. The call is tolerant of
  // failure — we continue either way. On localhost dev there is no real auth
  // server, so we just fall back to the sign-in page.
  private endSsoSession(): void {
    const methodName = 'endSsoSession() ';
    this.logger.info(methodName);
    if (this.urlTenantDomain && this.urlTenantDomain.includes('localhost')) {
      this.logger.info(methodName + 'localhost — skipping browser sign-out, go to sign-in page');
      this.navController.navigateRoot(['/sign-in']);
      return;
    }
    this.logoutService.endBrowserSsoSession().pipe(take(1))
    .subscribe(() => {
      this.logger.info(methodName + 'browser SSO session ended — continue to sign-in app');
      this.handleUnverifiedCredentials();
    });
  }

  private handleUnverifiedCredentials() {
    const methodName = 'handleUnverifiedCredentials() '
    this.logger.info(methodName);
    this.logger.info(methodName + 'url does not contain "code="');
    // Explains an ejection but does not force re-entry: this is where AuthSignalService lands
    // every 401/403 the interceptor could not refresh past, the ordinary case being a refresh
    // token that expired while the user was idle. The SSO session is usually still good, so
    // signing them straight back in is the right outcome — the failure was the app's stored
    // token, not their identity.
    this.goToSignIn(methodName, {
      explain: this.signOutParam === 'ACCESS_DENIED',
      forceReauthentication: false
    });
  }

  /**
   * Hands the user off to sign in, by platform: the sign-in web app on web, LoginPage on
   * native and in local development.
   *
   * Shared by the two ways stored credentials can turn out to be unusable — none found at
   * bootstrap ({@link handleUnverifiedCredentials}) and rejected afterwards
   * ({@link handleRejectedSession}) — so the two cannot drift apart.
   *
   * @param fromMethod - Caller name, for logging
   * @param options.explain - Whether to tell the user why they are being sent back: false for a
   *   bootstrap with no stored credentials, true when they had a session and are being ejected.
   *   A toast on web, a query parameter LoginPage renders inline on native. On web it delays the
   *   hand-off by ERROR_TOAST_MS, since the redirect would otherwise destroy the toast unread.
   * @param options.forceReauthentication - Whether the sign-in app must re-challenge rather than
   *   answer from the surviving SSO session. Separate from `explain` on purpose: every ejection
   *   is worth explaining, only one is worth making the user type their password again.
   */
  private goToSignIn(
    fromMethod: string,
    options: { explain: boolean; forceReauthentication: boolean }
  ): void {
    const { explain, forceReauthentication } = options;
    // Nothing renders on this page unless a state flag is set, and neither caller leaves
    // isLoading on. Everything below takes seconds — caption fetch, toast, buildSignInUrl() —
    // so without this the user waits on a blank page. Here rather than in the callers, because
    // both callers and both branches below need it.
    this.isLoading.set(true);
    if (!this.isCordova && !this.urlTenantDomain.includes('localhost')) { // Webapp, not in development
      if (explain) {
        this.explainThenHandOff(fromMethod, forceReauthentication);
      } else {
        // Passed through rather than left to the default: the two options are independent, and
        // an ejection that did not warrant an explanation would still warrant the challenge.
        this.handOffToSignInApp(fromMethod, forceReauthentication);
      }
    }  else {
      this.logger.info(fromMethod + 'Go to login page');
      // Forwarded rather than dropped: LoginPage is the destination on native, and without
      // this the same rejection explains itself when the user reached it by tapping sign-in
      // and says nothing when they reached it from a cold start. Same parameter
      // AuthSignalService already sends for a 401/403 ejection, which LoginPage.initPage()
      // now reads.
      this.navController.navigateRoot(['/sign-in'],
        explain ? { queryParams: { signOutParam: 'ACCESS_DENIED' } } : {});
    }
  }

  /**
   * Tells the user why they are being sent back, then hands off.
   *
   * The caption may not have loaded yet: this often runs on a fresh document load, before
   * LanguageService has called use(). instant() and get() would both resolve synchronously with
   * the bare key, so stream() is used — it re-emits when the captions land.
   *
   * If no caption can be had, the toast is skipped rather than shown untranslated; a raw
   * ACCESS_DENIED explains less than the sign-in screen the user is heading for. The hand-off
   * never waits on the message.
   *
   * @param fromMethod - Caller name, for logging
   * @param forceReauthentication - Passed through to the hand-off; see buildSignInUrl()
   */
  private explainThenHandOff(fromMethod: string, forceReauthentication: boolean): void {
    const key = 'ACCESS_DENIED';
    // stream() re-emits on onLangChange, which is when loaded captions arrive. filter ahead of
    // take(1), so the initial key echo is skipped rather than consumed.
    this.translateService.stream(key).pipe(
      filter((message: string) => !!message && message !== key),
      take(1),
      timeout(AuthPage.CAPTION_WAIT_MS),
      catchError(() => of(key))
    ).subscribe((message: string) => {
      if (!message || message === key) {
        this.logger.warn(fromMethod + 'no caption for ' + key + ' yet, handing off unexplained');
        this.handOffToSignInApp(fromMethod, forceReauthentication);
        return;
      }
      // catch() ahead of finally(), which re-rejects: otherwise a toast failure is an unhandled
      // rejection and nothing records that the explanation went missing.
      void this.showErrorToast(message)
        .catch((error) => this.logger.warn(fromMethod +
          'could not present the explanation, handing off anyway: ' + JSON.stringify(error)))
        .finally(() => this.handOffToSignInApp(fromMethod, forceReauthentication));
    });
  }

  /**
   * Starts the hand-off and makes sure a failure inside it is recorded.
   *
   * goToBrowserSignIn() is async and unawaited, so a throw in buildSignInUrl() — missing
   * endpoint, failed PKCE challenge — would be an unhandled rejection leaving the user on a
   * blank /auth with nothing logged. The redirect cannot be rescued here, only recorded.
   *
   * @param fromMethod - Caller name, for logging
   * @param forceReauthentication - Passed to buildSignInUrl(); see there
   */
  private handOffToSignInApp(fromMethod: string, forceReauthentication: boolean): void {
    this.goToBrowserSignIn(forceReauthentication).catch((error) => {
      this.logger.error(fromMethod + 'hand-off to the sign-in app failed: ' + JSON.stringify(error));
      // Stop the spinner: the redirect is not coming, and a spinner that never resolves claims
      // progress that will not happen. The page is back to blank, which is no worse than
      // before and at least is not a lie.
      this.isLoading.set(false);
    });
  }

  // Browser platform: hand off to the sign-in app (separate web app) and let it
  // own the IDP session. Unlike native — where login.page.ts catches the
  // idpLoginToken off the printixapp:/ deeplink and stores it via
  // TenantService.setIdpLoginToken() — on web this app never sees the idp token;
  // the sign-in app handles it and returns only the PKCE `code` we exchange here.
  private async goToBrowserSignIn(forceReauthentication: boolean = false): Promise<void> {
    this.logger.info('goToBrowserSignIn() forceReauthentication: ' + forceReauthentication);
    const signInApp_Url = await this.buildSignInUrl(forceReauthentication);
    this.logger.info('goToBrowserSignIn() - signInAppUrl: ' + signInApp_Url);
    window.open(signInApp_Url.toString(), '_self');
    // Testing registrationCode functionality in regard to Printix GO and on-device
    // append /?registrationCode=385480 or /?registrationCode=FORGOT_CODE after Tenant domain in the url
  }

  /**
   * @param forceReauthentication - Sets prompt=login, so the authorize endpoint cannot answer
   *   from the surviving SSO session. Set by handleRejectedSession() alone, where the token
   *   was accepted and the *tenant list* was refused: signOutUser('session-ended') leaves the
   *   auth server's cookie alone, so without this the endpoint re-issues a code with no user
   *   interaction, the exchange succeeds, the list is refused again, and the app cycles
   *   unattended. It does not stop a persistent refusal recurring — fresh credentials meet the
   *   same policy — it stops the app doing laps without the user.
   *
   *   Off everywhere else, including the far more common ejection through
   *   handleUnverifiedCredentials(), where the stored token expired and the SSO session is
   *   usually still good. Forcing re-entry there would charge every idle-timeout a password
   *   for a failure that was not theirs. LoginPage sets it unconditionally because native
   *   sign-in is interactive regardless.
   */
  private async buildSignInUrl(forceReauthentication: boolean = false): Promise<URL> {
    this.logger.info('buildSignInUrl()');
    const ENDPOINT_TENANT_DOMAIN = this.environmentService.getEnvironmentEndpoint['ENDPOINT_TENANT_DOMAIN'];
    const signInApp_Url = new URL(`${ENDPOINT_TENANT_DOMAIN}${this.urlTenantDomain}`);
    const redirectUrl = new URL(`${this.WEB_APP_SCHEME}${this.urlTenantDomain}`);

    if (this.registrationCode) {
      redirectUrl.searchParams.set('registrationCode', this.registrationCode);
    }

    SIGN_IN_QUERY_PARAMS.forEach(param => {
      const key = Object.keys(param)[0];
      let value = param[key];
      if (key === 'client_id') {
          value = this.isCordova ? CLIENT_ID_NATIVE : CLIENT_ID_BROWSER;
      }
      if (key === 'redirect_uri') {
          value = redirectUrl.toString();
      }
      signInApp_Url.searchParams.set(key, value);
    });

    if (forceReauthentication) {
      signInApp_Url.searchParams.set('prompt', 'login');
    }

    // Add PKCE parameters for enhanced security sign-in flow
    const codeChallenge = await this.pkceService.generatePkceChallenge();
    signInApp_Url.searchParams.set('code_challenge', codeChallenge);
    signInApp_Url.searchParams.set('code_challenge_method', 'S256');
    return signInApp_Url;
  }

  private handleUserAuthentication(): void {
    this.logger.info('handleUserAuthentication()');
    this.isLoading.set(true);
    // Pass registrationCode so the token-request redirect_uri matches the one the
    // authorization request used (RFC 6749 §4.1.3) on the GO-registration web flow.
    this.authService.exchangeCodeForTokens(this.signInCode, this.urlTenantDomain, this.registrationCode).pipe(take(1))
    .subscribe({
      next: () => {
        this.logger.info('handleUserAuthentication() - verified');
        this.authSuccessful();
      },
      error: (error) => {
        this.isLoading.set(false);
        this.logger.error('handleUserAuthentication() ERROR: ' + JSON.stringify(error));
        // Non-HTTP errors (e.g. PKCE code_verifier missing) are precondition failures,
        // not server errors — route back to /sign-in so the user can restart the flow
        // instead of seeing a generic HTTP error modal that doesn't fit the situation.
        if (!(error instanceof HttpErrorResponse)) {
          this.signInCode = null;
          if (!this.isCordova && !this.urlTenantDomain.includes('localhost')) {
            // Through the wrapper, not bare: this branch is reached *because* a precondition
            // failed, and buildSignInUrl() then calls generatePkceChallenge() again on the same
            // environment — so of the three hand-off sites this is the one most likely to
            // reject, and a rejection here leaves a blank /auth with nothing logged.
            this.handOffToSignInApp('handleUserAuthentication() ', false);
          } else {
            this.navController.navigateRoot(['/sign-in']);
          }
          return;
        }
        this.errorService.handleHttpClientResponseError(error, 'GET', 'handleUserAuthentication')
        .subscribe(result => {
          if (result === 'unauthenticated') {
            this.logger.info('User unauthenticated');
          }
        });
      }
    });
  }

  public authSuccessful() {
    this.logger.info('authSuccessful()');
    this.isLoading.set(true);
    let queryParams: { [key: string]: string } = {};
    this.route.queryParams.pipe(
      take(1),
      switchMap(params => {
        queryParams = params['registrationCode'] ? { registrationCode: params['registrationCode']} : {};
        return this.loginService.resolvePostAuthNavigation$().pipe(
          tap((destination: PostAuthDestination) => this.navigatePostAuth(destination, queryParams))
        );
      }),
      catchError(error => {
        this.isLoading.set(false);
        this.logger.error('authSuccessful() - Error during post-auth navigation - '
          + describePostAuthFailure(error));
        // A rejected session is not a tenant-selection problem. resolvePostAuthNavigation$()
        // already picks the tenant itself — url domain, then lastUsedTenant, then a
        // single-tenant list — and only returns 'tenant-select' when the choice is genuinely
        // ambiguous. So tenant-select is the right fallback for a failure *within* that
        // resolution, but not for a rejected tenant-list call, where nothing about the tenant
        // is unknown and the honest answer is that the stored credentials are dead.
        //
        // It also mattered beyond the wrong wording: bouncing to /tenant-select made
        // TenantSelectPage re-issue the same failing call, and that one goes through
        // ErrorService, which signs the user out. See the SignOutIntent split in
        // LogoutService for what that used to cost the pending return URL.
        if (error instanceof SessionRejectedError) {
          this.handleRejectedSession();
        } else {
          this.navController.navigateRoot(['/tenant-select'], { queryParams });
        }
        return of(null);
      })
    ).subscribe();
  }

  /**
   * Sends a user whose stored session turned out to be dead back to sign-in.
   *
   * Signs out first so the stale tokens cannot authenticate the next bootstrap — the same
   * thing ErrorService.handleAuthenticationError() does — with the 'session-ended' intent,
   * so the route the user was originally heading for survives to be consumed after they
   * sign in again. The destination is the one the app already reaches for this case, minus
   * the tenant-select detour, so the user-visible outcome is unchanged.
   */
  private handleRejectedSession(): void {
    const methodName = 'handleRejectedSession() ';
    this.logger.info(methodName + 'stored credentials rejected, returning to sign-in');
    // Before the sign-out, not inside goToSignIn(): the revoke round trip happens first, and
    // goToSignIn() runs only from the recovery callback. Two limits: the spinner is also gated
    // on isOnline(), and nothing bounds the revoke, so a stalled request leaves it spinning —
    // acceptable here because the alternative is a blank page, but it is why LoginPage does not
    // do the same.
    this.isLoading.set(true);
    // Both flags: the user is told why, and the sign-in app is made to re-challenge — see
    // buildSignInUrl() for why this caller in particular needs that. The hand-off runs whether
    // or not the revoke succeeded; see LogoutService.signOutAndRecover().
    this.logoutService.signOutAndRecover(
      () => this.goToSignIn(methodName, { explain: true, forceReauthentication: true }));
  }

  private navigatePostAuth(destination: PostAuthDestination, queryParams: { [key: string]: string }): void {
    if (destination === 'app') {
      const registrationCode = queryParams['registrationCode'];
      if (registrationCode) {
        this.postAuthNavigationService.navigateToCardRegistration(registrationCode);
        return;
      }
      this.postAuthNavigationService.navigateIntoApp();
    } else if (destination === 'no-access') {
      // Terminal for this session, so drop the stash rather than leave it to fire on a
      // later sign-in that has nothing to do with the route that was interrupted.
      this.postAuthNavigationService.discardReturnUrl();
      // We are already on the auth page (this component runs authSuccessful() for
      // returning users), so re-navigating to '/auth' would be reused without a
      // fresh ngOnInit. Enter the no-access state directly instead.
      this.enterNoAccessState();
    } else {
      this.navController.navigateRoot(['/tenant-select'], { queryParams });
    }
  }

  private logInFromTenantHub() {
    this.logger.info('logInFromTenantHub()');
    const refreshToken = location.hash.split('=')[1];
    this.authService.performTokenRefresh(refreshToken)
    .pipe(
      // Returning user via tenant-hub refresh — any idpLoginToken in storage
      // is leftover from a previous activation chain. Clear it before
      // authSuccessful() so the tenant-list call is not routed through
      // internal-auth validation.
      switchMap(() => this.loginService.clearIdpLoginToken$())
    )
    .subscribe({
      next: () => {
        this.authSuccessful();
      },
      error: (error) => {
        this.isLoading.set(false);
        this.logger.error('logInFromTenantHub() - performTokenRefresh failed: ' + JSON.stringify(error));
        this.handleUnverifiedCredentials();
      }
    });
  }

  /**
   * Shows an error and waits for it to go away again.
   *
   * The await on onDidDismiss() is the point: the only caller follows this with a document
   * navigation, which tears the toast down mid-animation. Without it, duration is meaningless
   * and the user is shown an explanation they cannot read.
   *
   * @param message - Already-translated text; this is handed straight to the toast
   */
  private async showErrorToast(message: string): Promise<void> {
    const toast = await this.toastController.create({
      message: message,
      color: 'danger',
      duration: AuthPage.ERROR_TOAST_MS,
      position: 'top'
    });
    await toast.present();
    await toast.onDidDismiss();
  }
}
